import { AnimatePresence } from 'motion/react';
import { useState, useMemo, useCallback } from 'react';
import { MODAL_VIEWS, ModalViewsKeys, type ModalViewsProps, type BaseModalViewsProps } from './constants';
import { ModalContext } from './Context';
import ErrorBoundary from '@/organisms/ErrorBoundary';

type ModalItem<T extends ModalViewsKeys> = {
  modalName: T;
  modalProps: ModalViewsProps<T>;
};

type StoreType = {
  modals: ModalItem<ModalViewsKeys>[];
};

const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [store, setStore] = useState<StoreType>({
    modals: [],
  });

  const openModal = useCallback(
    <T extends ModalViewsKeys>(name: T, props: ModalViewsProps<T>, replaceModal: boolean = false) => {
      const newModal: ModalItem<T> = {
        modalName: name,
        modalProps: props,
      };
      setStore((prevStore) => {
        return {
          modals: replaceModal ? [newModal] : [...prevStore.modals, newModal],
        };
      });
    },
    [],
  );

  const replaceModal = useCallback(<T extends ModalViewsKeys>(name: T, props: ModalViewsProps<T>) => {
    openModal(name, props, true);
  }, []);

  const closeAllModals = useCallback(() => {
    setStore((prevStore) => {
      return {
        ...prevStore,
        modals: [],
      };
    });
  }, []);

  const closeModal = useCallback((modalName?: string) => {
    if (!modalName) {
      closeAllModals();

      return;
    }

    setStore((prevStore) => {
      return {
        ...prevStore,
        modals: prevStore.modals.filter((modal) => modal.modalName !== modalName),
      };
    });
  }, []);

  const renderModals = () =>
    store.modals.map(({ modalName, modalProps }) => {
      const ModalComponent = MODAL_VIEWS[modalName] as React.ComponentType<
        BaseModalViewsProps<typeof modalName>
      > | null;

      return ModalComponent ? (
        <ModalComponent key={modalName} {...modalProps} closeModal={() => closeModal(modalName)} />
      ) : null;
    });

  const providerValue = useMemo(() => {
    return { modals: store.modals, openModal, closeModal, closeAllModals, replaceModal };
  }, [store.modals, openModal, closeModal, closeAllModals, replaceModal]);

  return (
    <ModalContext.Provider value={providerValue}>
      <AnimatePresence>
        <ErrorBoundary>{renderModals()}</ErrorBoundary>
      </AnimatePresence>
      {children}
    </ModalContext.Provider>
  );
};

export default ModalProvider;
